/**
 * The pages a turn's web searches used — the same whichever model searched
 * (Claude's native search, OpenAI's, Google's). The backend gathers them in
 * one shape (`shared/services/ai/web_sources.py`) and stores them on the
 * message as `message_metadata.web_sources`.
 *
 * Shown the way ChatGPT shows them (operator decision 2026-10-19): one small,
 * quiet "Sources" pill under the answer — a stack of up to three site icons
 * and the word — the height of the message action buttons. It opens a
 * compact list: icon, title and site per page, each a link to a new tab. A
 * page with no title of its own (Gemini names only the site) shows its
 * address instead of the site, so each row says which page it opens.
 *
 * The sources carry no favicons, and fetching them from a favicon service
 * would hand every site the user read to a third party (and break under the
 * CSP), so every site is shown with the same neutral globe.
 *
 * Keyboard: the pill is a button with aria-expanded; the list is a named
 * dialog. Opening it moves focus to the first link (the list is portalled,
 * so Tab could not reach it from the pill); Esc, tabbing out of the list or
 * a click elsewhere closes it and returns focus to the pill. The pill and list take the UI's direction; each title and site is
 * the page's own text in any script, isolated with <bdi>.
 */
import { useId, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { Popover } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import { useLanguage } from '@/hooks/useLanguage';
import type { WebSource } from '@/types/models';
import { mirrorPlacement } from '@/utils/rtl';
import { safeHttpUrl } from '@/utils/safeHttpUrl';
import './WebSources.css';

/** How many site icons the pill stacks. */
const STACKED_ICONS = 3;

interface WebSourcesProps {
  sources?: WebSource[] | null;
  testId?: string;
}

/**
 * A page's address as a reader recognises it: its site (as the server
 * normalised it) and its path, decoded. Shown under a page whose search gave
 * no title (Gemini names only the site), so two pages of one site are told
 * apart. The conversation export labels such a page the same way (`web_sources.page_address`).
 */
function pageAddress(url: string, site: string): string {
  try {
    const u = new URL(url);
    const path = `${u.pathname.replace(/\/$/, '')}${u.search}`;
    try { return `${site}${decodeURI(path)}`; } catch { return `${site}${path}`; }
  } catch {
    return url;
  }
}

function SiteIcon() {
  return (
    <span className="web-source-icon" aria-hidden>
      <GlobalOutlined />
    </span>
  );
}

export function WebSources({ sources, testId = 'web-sources' }: WebSourcesProps) {
  const { t, direction } = useLanguage();
  const [open, setOpen] = useState(false);
  const pillRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listId = useId();

  const shown = useMemo(
    () => (sources ?? [])
      .map((s) => (s ? { ...s, url: safeHttpUrl(s.url) } : null))
      .filter((s): s is WebSource => Boolean(s && s.url)),
    [sources],
  );
  if (shown.length === 0) return null;

  const stacked = Math.min(STACKED_ICONS, new Set(shown.map((s) => s.site || s.url)).size);
  const heading = t('chat.webSources.title');

  const closeToPill = () => {
    setOpen(false);
    pillRef.current?.focus();
  };

  const links = () => Array.from(listRef.current?.querySelectorAll<HTMLAnchorElement>('a') ?? []);

  const onListKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      closeToPill();
      return;
    }
    if (e.key !== 'Tab') return;
    const all = links();
    const leaving = e.shiftKey ? document.activeElement === all[0] : document.activeElement === all[all.length - 1];
    if (leaving) {
      e.preventDefault();
      closeToPill();
    }
  };

  // A named dialog: antd's popup wrapper is role="tooltip", which tells a
  // screen reader there is nothing to interact with inside.
  const list = (
    <div id={listId} role="dialog" aria-label={heading} className="web-sources-panel" dir={direction}
         onKeyDown={onListKeyDown} data-testid={`${testId}-list`}>
      <div className="web-sources-panel-title" aria-hidden>{heading}</div>
      <ul ref={listRef} className="web-sources-list">
        {shown.map((s) => {
          const site = s.site || s.url;
          const title = s.title || site;
          const untitled = title === site;
          return (
            <li key={s.url}>
              <a
                className="web-source"
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                title={t('chat.webSources.open', { title, site })}
              >
                <SiteIcon />
                <span className="web-source-text">
                  <bdi className="web-source-title" dir="auto">{title}</bdi>
                  <bdi className="web-source-site" dir={untitled ? 'ltr' : 'auto'}>
                    {untitled ? pageAddress(s.url, site) : site}
                  </bdi>
                </span>
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );

  return (
    <div className="web-sources" dir={direction} data-testid={testId}>
      <Popover
        content={list}
        trigger="click"
        open={open}
        onOpenChange={setOpen}
        afterOpenChange={(isOpen) => {
          if (isOpen) links()[0]?.focus();
          // Closed by a click elsewhere: focus was on a link that is now
          // hidden, so it would fall to <body>.
          else if (document.activeElement === document.body) pillRef.current?.focus({ preventScroll: true });
        }}
        placement={mirrorPlacement(direction, 'bottomLeft')}
        arrow={false}
        overlayClassName="web-sources-overlay"
      >
        <button
          ref={pillRef}
          type="button"
          className={`web-sources-pill${open ? ' is-open' : ''}`}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-label={t('chat.webSources.show', { count: shown.length })}
          onKeyDown={(e) => { if (e.key === 'Escape' && open) { e.preventDefault(); setOpen(false); } }}
          data-testid={`${testId}-pill`}
        >
          <span className="web-sources-stack" aria-hidden>
            {Array.from({ length: stacked }, (_, i) => <SiteIcon key={i} />)}
          </span>
          <span>{heading}</span>
        </button>
      </Popover>
    </div>
  );
}
